import {
  useConfigApp,
  useUserInfo,
  useAuthoringEnvironments,
  useHasPermissionsHelpers,
} from '@web-config-app/core-react';
import { checkModulePermissions } from '@web-config-app/core';

interface UseCanAccessModuleResult {
  canAccess: boolean;
  /** True once user info has loaded — while false, permission checks are indeterminate */
  isLoaded: boolean;
}

/**
 * Hook that checks module-level permissions for the current user.
 *
 * AND semantics: ALL specified permissions must be satisfied.
 * - No permissions or empty array → always accessible
 * - Contains 'admin' → requires isAdminUser() (email-domain allowlist)
 * - Non-admin strings → checked against the user's JWT claims via
 *   hasAllPermissions (same mechanism as entity endpoint security)
 */
export const useCanAccessModule = (
  permissions?: string[],
): UseCanAccessModuleResult => {
  const { tenantId, permissionsFilterComplete } = useConfigApp();
  const { isUserInfoLoaded } = useUserInfo();
  const { baseAuthoringEnvironment } = useAuthoringEnvironments();
  const { hasAllPermissions } = useHasPermissionsHelpers({
    environmentKey: baseAuthoringEnvironment.environmentKey,
    tenantId,
    isAuthenticated: true,
  });

  // Modules with no permissions are accessible to all — no need to wait for user info
  if (!permissions || permissions.length === 0) {
    return { canAccess: true, isLoaded: true };
  }

  if (!isUserInfoLoaded) {
    return { canAccess: false, isLoaded: false };
  }

  // Non-admin permissions depend on the JWT permissions query. While it's
  // in flight `hasAllPermissions` returns false for every claim, which
  // would trip PermissionsGuard into rendering ErrorPage for modules the
  // user is actually allowed to see. `permissionsFilterComplete` flips to
  // true once `useFilterEntitiesBasedOnPermissions` sees the query resolve.
  const needsJwtClaims = permissions.some((p) => p !== 'admin');
  if (needsJwtClaims && !permissionsFilterComplete) {
    return { canAccess: false, isLoaded: false };
  }

  return {
    canAccess: checkModulePermissions(permissions, hasAllPermissions),
    isLoaded: true,
  };
};
